import snakecaseKeys from 'snakecase-keys';

import { CamelCaseKeys, ProviderMedicalRole, ProviderPrescriberType, ProviderSignOnBonusTier, ProviderTerminationReason, ProviderTerminationType, UserRules } from '@uplift/types';

import { restClient } from '../../client';
import { ProviderDetail, User } from '../../resources';
import { RequiredTrainingTask } from '../provider/reliasTrainings';

export const enum ProviderActivationStatus {
  pendingInvite = 'pending_invite',
  inviteSent = 'invite_sent',
  onboarding = 'onboarding',
  readyToGo = 'ready_to_go',
  live = 'live',
  deactivated = 'deactivated',
}

export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';


export type ProviderActivationInviteSentTaskStatusMetadata = CamelCaseKeys<{
  reminders_sent: string[];
}>;


export const enum ProviderMatchingProfileBookmark {
  initial = 'initial',
  supportedAgeGroups = 'supported_age_groups',
  modalities = 'modalities',
  specialties = 'specialties',
  treatmentApproaches = 'treatment_approaches',
  communityExpertise = 'community_expertise',
  bio = 'bio',
  languages = 'languages',
  gender = 'gender',
  faiths = 'faiths',
  ethnicities = 'ethnicities',
  therapeuticStyles = 'therapeutic_styles',
  pronouns = 'pronouns',
  qualification = 'qualification',
  locations = 'locations',
  photo = 'photo',
  complete = 'complete',
}

export const enum ProviderActivationEventName {
  providerAdded = 'provider_added',
  signOnBonusAdded = 'sign_on_bonus_added',

  inviteSent = 'invite_sent',
  reminderSent = 'reminder_sent',

  referralSubmitted = 'referral_submitted',

  onboardingInitial = 'onboarding_initial',
  COIPDFExported = 'coi_pdf_exported',
  introductionVideoCompleted = 'introduction_video_completed',

  matchingProfileSubmitted = 'matching_profile_submitted',
  matchingProfileApproved = 'matching_profile_approved',

  matchingAvailabilitySubmitted = 'matching_availability_submitted',
  matchingAvailabilityApproved = 'matching_availability_approved',

  credentialingRequestSubmitted = 'credentialing_request_submitted',
  caqhInfoSubmitted = 'caqh_info_submitted',
  credentialingApproved = 'credentialing_approved',
  identifiersAdded = 'identifiers_added',
  insuranceAdded = 'insurance_added',
  licenseAdded = 'license_added',
  stateSelectionSubmitted = 'state_selection_submitted',
  checkrInvitationCompleted = 'checkr_invitation_completed',
  checkrReportSent = 'checkr_report_sent',
  checkrReportCompleted = 'checkr_report_completed',
  checkrReportReviewed = 'checkr_report_reviewed',

  mfaAdded = 'mfa_added',
  stripeConnectInProgress = 'stripe_connect_in_progress',
  stripeConnectCompleted = 'stripe_connect_completed',
  treatInviteSent = 'treat_invite_sent',
  labVendorInvitesSent = 'lab_vendor_invites_sent',

  readyToGo = 'ready_to_go',
  live = 'live',
  confettiDisplayed = 'confetti_displayed',

  deactivated = 'deactivated',

  terminated = 'terminated',

  credentialingRequestCompleted = 'credentialing_request_completed',
  recredentialingRequestCompleted = 'recredentialing_request_completed',
  trainingStarted = 'training_started',
  trainingSubmitted = 'training_submitted',
  trainingApproved = 'training_approved',
  securityTrainingSent = 'security_training_sent',
  securityTrainingReviewed = 'security_training_reviewed',
  hipaaTrainingSent = 'hipaa_training_sent',
  hipaaTrainingReviewed = 'hipaa_training_reviewed',
  clinicalTrainingSent = 'clinical_training_sent',
  clinicalTrainingReviewed = 'clinical_training_reviewed',
}

export const PROVIDER_ACTIVATION_COMPUTED_EVENT_NAMES = new Set<ProviderActivationEventName>([
  ProviderActivationEventName.licenseAdded,
  ProviderActivationEventName.insuranceAdded,
  ProviderActivationEventName.stripeConnectInProgress,
  ProviderActivationEventName.stripeConnectCompleted,
  ProviderActivationEventName.referralSubmitted,
  ProviderActivationEventName.stateSelectionSubmitted,
  ProviderActivationEventName.terminated,
]);

export const PROVIDER_ACTIVATION_EVENT_STATUS: Partial<Record<ProviderActivationEventName, ProviderActivationStatus>> = {
  [ProviderActivationEventName.providerAdded]: ProviderActivationStatus.pendingInvite,
  [ProviderActivationEventName.identifiersAdded]: ProviderActivationStatus.pendingInvite,
  [ProviderActivationEventName.signOnBonusAdded]: ProviderActivationStatus.pendingInvite,

  [ProviderActivationEventName.inviteSent]: ProviderActivationStatus.inviteSent,
  [ProviderActivationEventName.reminderSent]: ProviderActivationStatus.inviteSent,

  [ProviderActivationEventName.onboardingInitial]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.checkrReportSent]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.checkrInvitationCompleted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.checkrReportCompleted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.checkrReportReviewed]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.matchingProfileSubmitted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.matchingProfileApproved]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.matchingAvailabilitySubmitted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.matchingAvailabilityApproved]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.credentialingApproved]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.credentialingRequestSubmitted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.credentialingRequestCompleted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.caqhInfoSubmitted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.stateSelectionSubmitted]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.insuranceAdded]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.licenseAdded]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.mfaAdded]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.stripeConnectInProgress]: ProviderActivationStatus.onboarding,
  [ProviderActivationEventName.stripeConnectCompleted]: ProviderActivationStatus.onboarding,

  [ProviderActivationEventName.readyToGo]: ProviderActivationStatus.readyToGo,

  [ProviderActivationEventName.live]: ProviderActivationStatus.live,
  [ProviderActivationEventName.confettiDisplayed]: ProviderActivationStatus.live,
  [ProviderActivationEventName.recredentialingRequestCompleted]: ProviderActivationStatus.live,
  [ProviderActivationEventName.treatInviteSent]: ProviderActivationStatus.live,
  [ProviderActivationEventName.labVendorInvitesSent]: ProviderActivationStatus.live,

  [ProviderActivationEventName.deactivated]: ProviderActivationStatus.deactivated,
};

export type ActivationEventCheckName =
  | 'no_duplicate_event'
  | 'created_at_not_in_future'
  | 'valid_status_transition'
  | 'onboarding_tasks_complete'
  | 'required_trainings_complete';

export type ActivationEventCheckResult = {
  check: ActivationEventCheckName;
  passed: boolean;
  overridable: boolean;
  message: string;
};

export type ActivationEventPrecheck = CamelCaseKeys<{
  new_status: ProviderActivationStatus;
  status_changing: boolean;
  checks: ActivationEventCheckResult[];
}>;

export type ProviderActivationEvent = {
  providerId: string;
  eventName: ProviderActivationEventName;
  eventCount: number;
  createdBy: string | null;
  createdAt: string;
  overrideReason: string | null;
  jiraReference: string | null;
  metadata?: {
    referralId?: string;
    terminationType?: ProviderTerminationType;
    terminationReason?: ProviderTerminationReason;
    terminationDescription?: string;
  };
};

type BaseProviderActivationStatusDetails<
  Status extends ProviderActivationStatus,
  Extra extends Record<string, unknown> = {}
> = CamelCaseKeys<{
  provider_id: string;
  status: Status;
  status_changed_at: string | null;
  events: ProviderActivationEvent[];
  required_trainings: RequiredTrainingTask[];
} & Extra>;

export type ProviderActivationTasks = {
  tasksToDo: ProviderActivationEventName[];
  tasksToDoOptional: ProviderActivationEventName[];
  tasksToBeApproved: ProviderActivationEventName[];
};


export type ActivationStatusDetailsPendingInvite = BaseProviderActivationStatusDetails<ProviderActivationStatus.pendingInvite>;
export type ActivationStatusDetailsInviteSent = BaseProviderActivationStatusDetails<ProviderActivationStatus.inviteSent, {
  reminders_count: number;
  reminder_sent_at: string;
  invite_sent_at: string;
} | {
  reminders_count: 0;
  reminder_sent_at: null;
  invite_sent_at: string;
}>;
export type ActivationStatusDetailsOnboarding = BaseProviderActivationStatusDetails<ProviderActivationStatus.onboarding>;
export type ActivationStatusDetailsReadyToGo = BaseProviderActivationStatusDetails<ProviderActivationStatus.readyToGo>;
export type ActivationStatusDetailsLive = BaseProviderActivationStatusDetails<ProviderActivationStatus.live>;
export type ActivationStatusDetailsDeactivated = BaseProviderActivationStatusDetails<ProviderActivationStatus.deactivated>;
export type ActivationStatusDetailsByStatus = {
  [ProviderActivationStatus.pendingInvite]: ActivationStatusDetailsPendingInvite;
  [ProviderActivationStatus.inviteSent]: ActivationStatusDetailsInviteSent;
  [ProviderActivationStatus.onboarding]: ActivationStatusDetailsOnboarding;
  [ProviderActivationStatus.readyToGo]: ActivationStatusDetailsReadyToGo;
  [ProviderActivationStatus.live]: ActivationStatusDetailsLive;
  [ProviderActivationStatus.deactivated]: ActivationStatusDetailsDeactivated;
};
export type ProviderActivationStatusDetails = ActivationStatusDetailsByStatus[keyof ActivationStatusDetailsByStatus];
export type ProviderActivationItem<T extends ProviderActivationStatusDetails> = CamelCaseKeys<{
  provider: User;
  licenseStateIds: string[];
  details: ProviderDetail;
  rules: UserRules;
  status_details: T;
}>;

export const adminGetProvidersActivation = () => restClient.request<{
  data: ProviderActivationItem<ProviderActivationStatusDetails>[];
}>('/api/v1/admin/provider_activations');

export const adminGetProviderActivation = (providerId: string) => restClient.request<{
  data: ProviderActivationStatusDetails;
}>(`/api/v1/admin/provider_activation/${providerId}`, { method: 'GET' });


// Onboarding screens (cards, profile banner): the event name only.
export type AddProviderActivationEventParams = {
  eventName: ProviderActivationEventName;
};

export const adminAddProviderActivationEvent = (providerId: string, params: AddProviderActivationEventParams) => restClient.request<{
  data: null;
}>(`/api/v1/admin/provider_activation/${providerId}/events`, {
  method: 'POST',
  data: snakecaseKeys(params),
});

export type RemoveProviderActivationEventParams = {
  eventName: ProviderActivationEventName;
};

export const adminRemoveProviderActivationEvent = (providerId: string, params: RemoveProviderActivationEventParams) => restClient.request<{
  data: null;
}>(`/api/v1/admin/provider_activation/${providerId}/events`, {
  method: 'DELETE',
  data: snakecaseKeys(params),
});

// Events tab: events added or deleted by hand, with the extra options. Needs the Provider Activation Events permission.
export type AddManageProviderActivationEventParams = {
  eventName: ProviderActivationEventName;
  eventCount?: number;
  createdAt?: string;
  overrideReason?: string;
  jiraReference?: string;
  runSideEffects?: boolean;
};

export const adminAddManageProviderActivationEvent = (providerId: string, params: AddManageProviderActivationEventParams) => restClient.request<{
  data: null;
}>(`/api/v1/admin/provider_activation/manage/${providerId}/events`, {
  method: 'POST',
  data: snakecaseKeys(params),
});

export type RemoveManageProviderActivationEventParams = {
  eventName: ProviderActivationEventName;
  eventCount?: number;
  overrideReason?: string;
  jiraReference?: string;
};

export const adminRemoveManageProviderActivationEvent = (providerId: string, params: RemoveManageProviderActivationEventParams) => restClient.request<{
  data: null;
}>(`/api/v1/admin/provider_activation/manage/${providerId}/events`, {
  method: 'DELETE',
  data: snakecaseKeys(params),
});

export type PrecheckProviderActivationEventParams = {
  eventName: ProviderActivationEventName;
  eventCount?: number;
  createdAt?: string;
};

export const adminPrecheckProviderActivationEvent = (providerId: string, params: PrecheckProviderActivationEventParams) => restClient.request<{
  data: ActivationEventPrecheck;
}>(`/api/v1/admin/provider_activation/${providerId}/events/precheck`, {
  method: 'POST',
  data: snakecaseKeys(params),
});

export type PrecheckDeleteProviderActivationEventParams = {
  eventName: ProviderActivationEventName;
  eventCount?: number;
};

export const adminPrecheckDeleteProviderActivationEvent = (providerId: string, params: PrecheckDeleteProviderActivationEventParams) => restClient.request<{
  data: ActivationEventPrecheck;
}>(`/api/v1/admin/provider_activation/${providerId}/events/delete_precheck`, {
  method: 'POST',
  data: snakecaseKeys(params),
});

export type UpdateProviderActivationEventCreatedAtParams = {
  eventName: ProviderActivationEventName;
  eventCount?: number;
  createdAt: string;
};

export const adminUpdateProviderActivationEventCreatedAt = (providerId: string, params: UpdateProviderActivationEventCreatedAtParams) => restClient.request<{
  data: null;
}>(`/api/v1/admin/provider_activation/${providerId}/events`, {
  method: 'PATCH',
  data: snakecaseKeys(params),
});

export const adminMarkProviderTrainingCompleted = (providerId: string, trainingId: string) =>
  restClient.request<{ data: null }>(`/api/v1/admin/provider/${providerId}/training/${trainingId}/mark_complete`, {
    method: 'POST',
  });

export const adminGetProviderActivationTasks = (activationStatus: ProviderActivationStatus) => restClient.request<{
  data: ProviderActivationTasks;
}>(`/api/v1/admin/provider_activation/${activationStatus}/tasks`);

export type GetProviderActivationEventsResponse = {
  data: ProviderActivationEvent[];
};

export const adminGetProviderActivationEvents = (providerId: string) =>
  restClient.request<GetProviderActivationEventsResponse>(`/api/v1/admin/provider_activation/${providerId}/events`);

export const adminGetAddableProviderActivationEvents = () => restClient.request<{
  data: ProviderActivationEventName[];
}>('/api/v1/admin/provider_activation/events/addable');

export type Tag = {
  id: string;
  type: string;
  label: string | null;
};


export const getMSOData = () => restClient.request<{
  data: {
    partners: Tag[];
    groups: Tag[];
  };
}>('/api/v1/admin/mso');


export const adminAddProvider = (data: {
  firstName: string;
  lastName: string;
  emailAddress: string;
  fullTime: boolean;
  medicalRole: ProviderMedicalRole;
  prescriberType: ProviderPrescriberType | null;
  collaborativePhysician: boolean;
  msoPartner: string | null;
  msoGroups: string[] | null;
  tagIds?: string[] | null;
  signOnBonusTier: ProviderSignOnBonusTier | null;
}) =>
  restClient.request('/api/v1/admin/add_provider', {
    method: 'POST',
    data: snakecaseKeys(data),
  });

export const adminAddProviderSignOnBonus = (providerId: string, signOnBonusTier: ProviderSignOnBonusTier) =>
  restClient.request<{ data: null }>(`/api/v1/admin/providers/${providerId}/sign_on_bonus`, {
    method: 'POST',
    data: { sign_on_bonus_tier: signOnBonusTier },
  });
